
import { defineComponent, nextTick, ref } from "vue";
import type { ElAutocomplete, ElInput } from "element-plus";
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from "@/core/helpers/breadcrumb";
import {
  getErrorMsg,
  lengthValidationRules,
  swalErrNotification,
  swalNotification,
} from "@/core/helpers/utils";
import { useCreatingForm } from "@/core/hooks/use-creating-form";
import { useRouterCustom } from "@/core/hooks/use-router-custom";
import { useAccount } from "@/core/hooks/use-account";
import useCapDonVi from "@/core/hooks/use-cap-don-vi";
import {
  ICapSuaChuaResponseBase,
  IDanhMucToaXeResponseBase,
  IDonViResponseBase,
  IKeHoachSuaChuaResponseBase,
} from "@/core/interfaces/ApiResponses";
import { useLoading } from "@/core/hooks/use-loading";
import { KeHoachNamService } from "@/core/services/KeHoachNam.service";
import { CapSuaChuaService } from "@/core/services/CapSuaChuaService.service";
import { DonViService } from "@/core/services/DonVi.service";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import {
  formatTenKeHoachSuaChua,
  KE_HOACH_LOAI_KY,
} from "@/core/helpers/ke-hoach-sua-chua";

import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import DanhMucToaXeService from "@/core/services/DanhMucToaXe.service";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import DivRow from "@/layout/_shared/DivRow.vue";
import DivCol from "@/layout/_shared/DivCol.vue";
import { DinhMucCoDinhService } from "@/core/services/DinhMucCoDinh.service";
import { IDinhMucCoDinhNewResponseBase } from "@/core/interfaces/ApiResponses";
import DinhMucCoDinhTable from "@/components/dinh-muc-co-dinh/dinh-muc-co-dinh-table/DinhMucCoDinhTable.vue";
import Swal from "sweetalert2";
import useFormInput from "@/core/hooks/use-form-input";
import { mapMutations } from "vuex";
import { usePagination } from "@/core/hooks/use-pagination";
import KeHoachNamTable from "@/views/crafted/pages/ke-hoach-nam/ke-hoach-table/KeHoachNamTable.vue";
import Pagination from "@/components/shared/pagination/Pagination.vue";

type DanhMucToaXeSuggestionItem = IDanhMucToaXeResponseBase & {
  __loading?: boolean;
  __empty?: boolean;
};

type AutocompleteInstance = InstanceType<typeof ElAutocomplete> & {
  activated: boolean;
  suggestionDisabled: boolean;
};

const LOADING_DANH_MUC_TOA_XE_SUGGESTION: DanhMucToaXeSuggestionItem = {
  __loading: true,
  id: -1,
  ten_danh_muc: "Đang tìm kiếm...",
  ma_danh_muc: "",
} as DanhMucToaXeSuggestionItem;

const EMPTY_DANH_MUC_TOA_XE_SUGGESTION: DanhMucToaXeSuggestionItem = {
  __empty: true,
  id: -2,
  ten_danh_muc: "Không có kết quả phù hợp!",
  ma_danh_muc: "",
} as DanhMucToaXeSuggestionItem;

export default defineComponent({
  name: "ke-hoach-nam-form",

  components: {
    ButtonCustom,
    DinhMucCoDinhTable,
    DivCol,
    DivRow,
    KeHoachNamTable,
    LabelCustom,
    PageContentBase,
    Pagination,
  },

  setup() {
    const { isCreating, formRef } = useCreatingForm();
    const { goBack, replace, id } = useRouterCustom();
    const { startLoading, endLoading, isLoading } = useLoading();
    const { ButtonTypeColors, ButtonsType } = useButtonCustom();
    const { focusFirstErrInputV2 } = useFormInput();
    const { currentPage, perPage, totalItems } = usePagination();
    const { currentUserData } = useAccount();
    const { isCompanyLevel, isUnitLevel } = useCapDonVi();

    const danhMucToaXeAutocompleteRef = ref<AutocompleteInstance>();
    const soVanBanInputRef = ref<InstanceType<typeof ElInput>>();

    const focusSoVanBanInput = async () => {
      await nextTick();
      soVanBanInputRef.value?.focus();
      soVanBanInputRef.value?.select?.();
    };

    return {
      currentPage,
      currentUserData,
      danhMucToaXeAutocompleteRef,
      focusFirstErrInputV2,
      focusSoVanBanInput,
      idKeHoach: id,
      isCompanyLevel,
      isCreating,
      isLoading,
      isUnitLevel,
      goBack,
      replace,
      formRef,
      perPage,
      soVanBanInputRef,
      startLoading,
      endLoading,
      totalItems,
      ButtonTypeColors,
      ButtonsType,
    };
  },

  data() {
    return {
      dinhMucCoDinhDetail: null as IDinhMucCoDinhNewResponseBase | null,
      keHoachItems: [] as IKeHoachSuaChuaResponseBase[],
      tenDanhMucToaXe: "",
      maDanhMucToaXe: "",
      capSuaChuaItems: [] as ICapSuaChuaResponseBase[],
      chiNhanhItems: [] as IDonViResponseBase[],
      isLoadingDmcdData: false,
      rules: {
        id_don_vi: [
          {
            type: "number",
            min: 1,
            message: "Phải chọn chi nhánh",
            trigger: "change",
          },
        ],
        cap_sua_chua_id: [
          {
            type: "number",
            min: 1,
            message: "Phải chọn cấp sửa chữa",
            trigger: "change",
          },
        ],
        id_danh_muc_toa_xe: [
          {
            type: "number",
            min: 1,
            message: "Phải chọn nhóm toa xe",
            trigger: "change",
          },
        ],
        so_van_ban: [
          {
            type: "string",
            max: 100,
            message: "Số văn bản tối đa 100 ký tự",
            trigger: "change",
          },
        ],
        ghi_chu: lengthValidationRules({
          isRequired: false,
          max: 150,
          name: "Ghi chú",
        }),
        so_luong_toa_xe: [
          {
            type: "number",
            min: 1,
            message: "Số lượng toa xe phải lớn hơn 0",
            trigger: "blur",
          },
          {
            type: "number",
            min: 1,
            message: "Số lượng toa xe phải lớn hơn 0",
            trigger: "change",
          },
        ],
      },

      form: {
        cap_sua_chua_id: 0,
        id_don_vi: 0,
        so_van_ban: "",
        ngay_ban_hanh: null as Date | null,
        id_danh_muc_toa_xe: 0,
        so_luong_toa_xe: 1,
        ghi_chu: "",
      },
    };
  },

  computed: {
    workingYear(): string {
      return (
        this.$store.getters.getSelectedYear ||
        new Date().getFullYear().toString()
      );
    },
    selectedCapSuaChua(): ICapSuaChuaResponseBase | undefined {
      return this.capSuaChuaItems.find(
        (item) => item.id === this.form.cap_sua_chua_id
      );
    },
    tenKeHoachDisplay(): string {
      return formatTenKeHoachSuaChua({
        loaiKy: KE_HOACH_LOAI_KY.NAM,
        nam: this.workingYear,
        tenVietTat: this.selectedCapSuaChua?.ten_viet_tat || "",
        maDanhMuc: this.maDanhMucToaXe,
      });
    },
    watchedValues() {
      return {
        capSuaChuaId: this.form.cap_sua_chua_id,
        danhMucToaXeId: this.form.id_danh_muc_toa_xe,
      };
    },
  },

  watch: {
    async $route() {
      this.startLoading();

      try {
        await this.syncRouteState();
      } finally {
        this.endLoading();
      }
    },
    tenKeHoachDisplay() {
      this.updatePageMeta();
    },
    watchedValues: {
      async handler() {
        if (!this.form.cap_sua_chua_id || !this.form.id_danh_muc_toa_xe) {
          this.dinhMucCoDinhDetail = null;
          return;
        }

        this.isLoadingDmcdData = true;

        try {
          this.dinhMucCoDinhDetail = await this.fetchDinhMucCoDinh(true);
        } finally {
          this.isLoadingDmcdData = false;
        }
      },
      deep: true,
    },
    async currentPage() {
      await this.fetchKeHoachList();
    },
    async perPage() {
      await nextTick();

      if (this.currentPage === 1) {
        await this.fetchKeHoachList();

        return;
      }

      this.currentPage = 1;
    },
  },

  async mounted() {
    this.startLoading();

    try {
      const {
        data: {
          data: { data: capSuaChuaResponse },
        },
      } = await CapSuaChuaService.list({});

      this.capSuaChuaItems = capSuaChuaResponse;

      const {
        data: { data: chiNhanhResponse },
      } = await DonViService.fetchDepartmentsByCapDonVi(2);

      this.chiNhanhItems = chiNhanhResponse || [];

      if (this.isUnitLevel) {
        this.form.id_don_vi = this.resolveDefaultIdDonVi();
      }

      await this.syncRouteState();

      if (this.isCreating) {
        this.form.id_don_vi = this.resolveDefaultIdDonVi();
      }
    } finally {
      this.endLoading();
    }

    await this.fetchKeHoachList();

    await this.focusSoVanBanInput();
  },

  methods: {
    ...mapMutations("ConfirmationDialogModule", {
      setDialog: "setDialog",
      closeDialog: "closeDialog",
    }),
    resolveDefaultIdDonVi() {
      // Unit-level users always belong to their own chi nhánh
      if (this.isUnitLevel) {
        const departmentId = this.currentUserData?.departmentId as
          | number
          | undefined;

        return departmentId && departmentId > 0 ? departmentId : 0;
      }

      // Company-level users must explicitly pick a chi nhánh
      return 0;
    },
    isEditRoute(route = this.$route) {
      return route.name?.toString().includes("chinh-sua") ?? false;
    },
    updatePageMeta() {
      setBreadcrumbLinks([
        {
          text: "Kế hoạch sửa chữa năm",
          routeData: { name: "danh-sach-ke-hoach-nam" },
        },
        ...(this.isCreating
          ? []
          : [
              {
                text: "Chi tiết",
                routeData: {
                  name: "chi-tiet-ke-hoach-nam",
                  params: {
                    id: this.idKeHoach,
                  },
                },
              },
            ]),
        {
          text: this.isCreating ? "Tạo mới" : "Chỉnh sửa",
        },
      ]);

      document.title = this.isCreating
        ? `${
            this.tenKeHoachDisplay || "Tạo mới"
          } - Kế hoạch sửa chữa năm - Đường sắt Hà Nội`
        : `${
            this.tenKeHoachDisplay || this.form.so_van_ban || ""
          } - Chỉnh sửa - Kế hoạch sửa chữa năm - Đường sắt Hà Nội`;

      setCurrentPageTitle(
        this.isCreating
          ? this.tenKeHoachDisplay || "Tạo mới kế hoạch năm"
          : this.tenKeHoachDisplay ||
              this.form.so_van_ban ||
              "Chỉnh sửa kế hoạch năm"
      );
    },
    async syncRouteState() {
      const isEdit = this.isEditRoute();
      const wasEditing = !this.isCreating;

      this.isCreating = !isEdit;

      if (isEdit) {
        this.idKeHoach = +this.$route.params.id;
        await this.fetchKeHoach();
      } else if (wasEditing) {
        this.idKeHoach = 0;
        await this.clearForm(true);
      }

      this.updatePageMeta();
    },
    async clearForm(clearAllFields = false) {
      if (clearAllFields) {
        this.formRef?.resetFields();
        this.form.id_danh_muc_toa_xe = 0;
        this.tenDanhMucToaXe = "";
        this.maDanhMucToaXe = "";
        this.dinhMucCoDinhDetail = null;
        this.form.id_don_vi = this.resolveDefaultIdDonVi();

        await nextTick();
        this.formRef?.clearValidate();

        return;
      }

      const idDonVi = this.isUnitLevel
        ? this.resolveDefaultIdDonVi()
        : this.form.id_don_vi;

      Object.assign(this.form, {
        cap_sua_chua_id: 0,
        id_don_vi: idDonVi,
        so_van_ban: "",
        ngay_ban_hanh: null,
        id_danh_muc_toa_xe: 0,
        so_luong_toa_xe: 1,
        ghi_chu: "",
      });

      this.tenDanhMucToaXe = "";
      this.maDanhMucToaXe = "";
      this.dinhMucCoDinhDetail = null;

      await nextTick();
      this.formRef?.clearValidate();
    },
    async fetchKeHoach() {
      try {
        const {
          data: {
            data: {
              so_van_ban,
              ngay_ban_hanh,
              ghi_chu,
              danh_muc_toa_xe: {
                ten_danh_muc: tenDanhMucToaXe,
                ma_danh_muc: maDanhMucToaXe,
                id: idDanhMucToaXe,
              },
              so_luong_toa_xe,
              cap_sua_chua: { id: idCapSuaChua },
              don_vi: { id: idDonVi },
            },
          },
        } = await KeHoachNamService.get(this.idKeHoach);

        this.form.so_van_ban = so_van_ban;
        this.form.ngay_ban_hanh = ngay_ban_hanh
          ? new Date(ngay_ban_hanh)
          : null;
        this.form.ghi_chu = ghi_chu;
        this.form.so_luong_toa_xe = so_luong_toa_xe;
        this.form.id_don_vi = idDonVi;
        this.maDanhMucToaXe = maDanhMucToaXe || "";
        this.tenDanhMucToaXe = maDanhMucToaXe
          ? `${tenDanhMucToaXe} - (${maDanhMucToaXe})`
          : tenDanhMucToaXe;
        this.form.id_danh_muc_toa_xe = idDanhMucToaXe;
        this.form.cap_sua_chua_id = idCapSuaChua;
        this.updatePageMeta();
      } catch (e) {
        swalErrNotification(
          e,
          "Có lỗi khi lấy thông tin kế hoạch sửa chữa năm"
        );
      }
    },

    async fetchKeHoachList() {
      try {
        this.startLoading();

        const {
          data: {
            data: { data, total },
          },
        } = await KeHoachNamService.list({
          current_page: this.currentPage,
          per_page: this.perPage,
        });

        this.keHoachItems = data;
        this.totalItems = total;
        this.endLoading();
      } catch (err) {
        this.endLoading();

        await swalNotification(
          getErrorMsg(
            err,
            "Có lỗi xảy ra khi lấy thông tin kế hoạch sửa chữa năm"
          ),
          "error"
        );
      }
    },

    isStatusDanhMucToaXeSuggestion(item: DanhMucToaXeSuggestionItem) {
      return item.__loading || item.__empty;
    },

    async queryDanhMucToaXe(
      queryString: string,
      cb: (results: DanhMucToaXeSuggestionItem[]) => void
    ) {
      cb([LOADING_DANH_MUC_TOA_XE_SUGGESTION]);

      try {
        const {
          data: {
            data: { data },
          },
        } = await DanhMucToaXeService.list(1, 1000, queryString);

        if (!data?.length) {
          cb([EMPTY_DANH_MUC_TOA_XE_SUGGESTION]);

          return;
        }

        cb(data);
      } catch (error) {
        swalErrNotification(error, "Có lỗi xảy ra");
        cb([EMPTY_DANH_MUC_TOA_XE_SUGGESTION]);
      }
    },

    onClearDanhMucToaXe() {
      this.form.id_danh_muc_toa_xe = 0;
      this.tenDanhMucToaXe = "";
      this.maDanhMucToaXe = "";
      this.reopenDanhMucToaXeSuggestionsAfterClear();
    },

    reopenDanhMucToaXeSuggestionsAfterClear() {
      nextTick(() => {
        const autocomplete = this.danhMucToaXeAutocompleteRef;

        if (!autocomplete) {
          return;
        }

        autocomplete.suggestionDisabled = false;
        autocomplete.activated = true;
        autocomplete.getData("");
        autocomplete.focus();
      });
    },

    onSelectDanhMucToaXe(item: DanhMucToaXeSuggestionItem) {
      if (this.isStatusDanhMucToaXeSuggestion(item)) {
        return;
      }

      this.form.id_danh_muc_toa_xe = item.id;
      this.maDanhMucToaXe = item.ma_danh_muc || "";
      this.tenDanhMucToaXe = `${item.ten_danh_muc} - (${item.ma_danh_muc})`;
    },

    async fetchDinhMucCoDinh(
      silent = false
    ): Promise<IDinhMucCoDinhNewResponseBase | null> {
      if (!this.form.cap_sua_chua_id || !this.form.id_danh_muc_toa_xe) {
        return null;
      }

      try {
        const response =
          await DinhMucCoDinhService.getDinhMucCoDinhByIdCapSuaChuaAndDMTX({
            id_cap_sc: this.form.cap_sua_chua_id,
            id_dm_toa_xe: this.form.id_danh_muc_toa_xe,
          });

        if (!response) {
          return null;
        }

        const {
          data: { data },
        } = await DinhMucCoDinhService.get(response.id);

        return data;
      } catch (e) {
        if (!silent) {
          swalErrNotification(e, "Có lỗi khi lấy thông tin ĐMCĐ");
        }

        return null;
      }
    },

    async promptCreateDinhMucCoDinh(): Promise<boolean> {
      const result = await Swal.mixin({
        customClass: {
          confirmButton: "btn btn-success",
          cancelButton: "btn btn-warning",
        },
        buttonsStyling: false,
      }).fire({
        title: "Thông báo",
        icon: "warning",
        text: "Không tìm thấy ĐMCĐ cho cấp sửa chữa và nhóm toa xe này",
        showCancelButton: true,
        confirmButtonText: "Tạo mới ĐMCĐ",
        cancelButtonText: "Đóng",
      });

      return result.isConfirmed;
    },

    async submit(isRedirect: boolean) {
      if (!this.formRef) {
        return;
      }

      this.formRef.validate(async (valid) => {
        if (valid) {
          this.setDialog({
            message: this.isCreating
              ? "Bạn có chắc chắn muốn tạo mới Kế hoạch với các thông tin đã nhập?"
              : "Bạn có chắc chắn muốn cập nhật Kế hoạch với các thông tin đã thay đổi?",
            onConfirm: async () => {
              this.closeDialog();

              try {
                const {
                  cap_sua_chua_id: id_cap_sc,
                  so_van_ban,
                  ngay_ban_hanh,
                  ghi_chu,
                  so_luong_toa_xe,
                  id_danh_muc_toa_xe,
                  id_don_vi,
                } = this.form;

                this.startLoading();

                const {
                  data: {
                    data: { id: idKeHoach },
                  },
                } = await KeHoachNamService.createOrUpdate({
                  so_van_ban,
                  ngay_ban_hanh,
                  ghi_chu,
                  ten_ke_hoach: this.tenKeHoachDisplay,
                  id_cap_sc,
                  id_don_vi,
                  id_danh_muc_toa_xe,
                  so_luong_toa_xe,
                  isCreating: this.isCreating,
                  ...(!this.isCreating && {
                    id_ke_hoach: this.idKeHoach,
                  }),
                });

                this.idKeHoach = idKeHoach;
                this.endLoading();

                await swalNotification(
                  `${
                    this.isCreating ? "Tạo mới" : "Chỉnh sửa"
                  } kế hoạch thành công`,
                  "success"
                );

                if (isRedirect) {
                  this.replace("/quan-ly/ke-hoach-nam/danh-sach");

                  return;
                }

                await this.clearForm();

                if (this.currentPage !== 1) {
                  this.currentPage = 1;

                  return;
                }

                await this.fetchKeHoachList();
              } catch (e) {
                this.endLoading();

                await swalNotification(
                  getErrorMsg(
                    e,
                    `Có lỗi khi ${
                      this.isCreating ? "tạo mới" : "chỉnh sửa"
                    } kế hoạch`
                  ),
                  "error"
                );
              }
            },
          });
        } else {
          await this.focusFirstErrInputV2();

          return false;
        }
      });
    },
  },
});
